import { AbsoluteLayout, Component, Structure } from '@wix/thunderbolt-becky-types'
import * as _ from 'lodash'
import { PINNED_LAYER_SUFFIX } from '../constants'

const GROUP_TYPES = {
	LEGACY_GAPS: 'LEGACY_GAPS',
	PAGES_CONTAINER: 'PAGES_CONTAINER',
	PINNED: 'PINNED',
	SOAP: 'SOAP',
	STRUCTURAL: 'STRUCTURAL',
} as const

const PINNED_POSITIONS = {
	TOP_LEFT: 'TOP_LEFT',
	TOP_CENTER: 'TOP_CENTER',
	TOP_RIGHT: 'TOP_RIGHT',
	CENTER_LEFT: 'CENTER_LEFT',
	CENTER_RIGHT: 'CENTER_RIGHT',
	BOTTOM_LEFT: 'BOTTOM_LEFT',
	BOTTOM_CENTER: 'BOTTOM_CENTER',
	BOTTOM_RIGHT: 'BOTTOM_RIGHT',
}

export type MasterPageGroup = {
	type: keyof typeof GROUP_TYPES
	components: Array<string>
	id?: string
}

type ComponentsByType = {
	pinned: Record<string, Array<string>>
	soap: Record<string, Array<string>>
	structural: Array<string>
}

const isFixed = (compLayout: Component['layout']) => compLayout && compLayout.fixedPosition
const isPinned = (compLayout: Partial<AbsoluteLayout>) => isFixed(compLayout) && !!compLayout.docked

const shouldAddPlaceholder = (compId: string, layout: Component['layout']) =>
	(compId === 'SITE_HEADER' || compId === 'SITE_FOOTER') && isFixed(layout)

const createPinnedGroup = (compId: string) => ({
	type: GROUP_TYPES.PINNED,
	components: [compId],
	id: `${compId}${PINNED_LAYER_SUFFIX}`,
})
const createSOAPGroup = (components: Array<string>, groupId: string): MasterPageGroup => ({
	type: GROUP_TYPES.SOAP,
	components,
	id: groupId,
})
const createStructuralGroup = (compId: string): MasterPageGroup => ({
	type: GROUP_TYPES.STRUCTURAL,
	components: [compId],
})

const getIsStructural = (isMobileView: boolean) => {
	const structuralIDs: { [compId: string]: boolean } = isMobileView
		? {
				SITE_HEADER: true,
				SITE_FOOTER: true,
				PAGES_CONTAINER: true,
				BACK_TO_TOP_BUTTON: true,
				SOSP_CONTAINER_CUSTOM_ID: true,
				QUICK_ACTION_BAR: true,
		  }
		: { SITE_HEADER: true, SITE_FOOTER: true, PAGES_CONTAINER: true, BACK_TO_TOP_BUTTON: true }
	return (id: string) => Boolean(structuralIDs![id])
}

const getPinnedPosition = (compLayout: Partial<AbsoluteLayout>) => {
	const dockedData = compLayout.docked || {}

	if (dockedData.top) {
		// pinned to top
		if (dockedData.hCenter) {
			return PINNED_POSITIONS.TOP_CENTER
		} else {
			return dockedData.left ? PINNED_POSITIONS.TOP_LEFT : PINNED_POSITIONS.TOP_RIGHT
		}
	} else if (dockedData.vCenter) {
		// pinned to center
		return dockedData.left ? PINNED_POSITIONS.CENTER_LEFT : PINNED_POSITIONS.CENTER_RIGHT
	} else {
		// pinned to bottom
		if (dockedData.hCenter) {
			return PINNED_POSITIONS.BOTTOM_CENTER
		} else {
			return dockedData.left ? PINNED_POSITIONS.BOTTOM_LEFT : PINNED_POSITIONS.BOTTOM_RIGHT
		}
	}
}

const getMasterPageChildrenGroupsForMesh = (
	childrenIds: Array<string>,
	structure: Structure,
	isMobileView: boolean
) => {
	const isStructural = getIsStructural(isMobileView)
	return childrenIds.map((childrenId) => {
		if (isStructural(childrenId)) {
			return {
				type: 'STRUCTURAL',
				components: [childrenId],
			}
		}
		return {
			type: 'SOAP',
			components: structure[childrenId].components?.map((compId) => structure[compId]),
			id: childrenId,
		}
	})
}

const getMasterPageChildrenGroups = (
	childrenIds: Array<string>,
	childrenLayout: { [compId: string]: Component['layout'] },
	isMobileView: boolean
): Array<MasterPageGroup> => {
	const isStructural = getIsStructural(isMobileView)
	const pagesContainerIdx = childrenIds.findIndex((childId) => childId === 'PAGES_CONTAINER')
	const pinnedPositions = Object.values(PINNED_POSITIONS).map((pos) => ({ [pos]: [] }))

	const componentsByType = childrenIds.reduce(
		(acc, childId, idx) => {
			if (isStructural(childId)) {
				acc.structural.push(childId)
			} else {
				const childLayout = childrenLayout[childId]
				if (isPinned(childLayout)) {
					acc.pinned[getPinnedPosition(childLayout)].push(childId)
				} else if (!isFixed(childLayout)) {
					acc.soap[idx < pagesContainerIdx ? 'beforeMain' : 'afterMain'].push(childId)
				}
			}

			return acc
		},
		{
			pinned: Object.assign({}, ...pinnedPositions),
			soap: {
				beforeMain: [],
				afterMain: [],
			},
			structural: [],
		} as ComponentsByType
	)

	return [
		...componentsByType.pinned[PINNED_POSITIONS.TOP_LEFT].map((id) => createPinnedGroup(id)),
		...componentsByType.pinned[PINNED_POSITIONS.TOP_CENTER].map((id) => createPinnedGroup(id)),
		...componentsByType.pinned[PINNED_POSITIONS.TOP_RIGHT].map((id) => createPinnedGroup(id)),
		...(componentsByType.structural.includes('SITE_HEADER') ? [createStructuralGroup('SITE_HEADER')] : []),
		...(componentsByType.structural.includes('SOSP_CONTAINER_CUSTOM_ID')
			? [createStructuralGroup('SOSP_CONTAINER_CUSTOM_ID')]
			: []),
		...componentsByType.pinned[PINNED_POSITIONS.CENTER_LEFT].map((id) => createPinnedGroup(id)),
		...componentsByType.pinned[PINNED_POSITIONS.CENTER_RIGHT].map((id) => createPinnedGroup(id)),
		...(componentsByType.soap.beforeMain.length > 0
			? [createSOAPGroup(componentsByType.soap.beforeMain, 'soapBeforePagesContainer')]
			: []),
		...(componentsByType.structural.includes('PAGES_CONTAINER') ? [createStructuralGroup('PAGES_CONTAINER')] : []),
		...(componentsByType.soap.afterMain.length > 0
			? [createSOAPGroup(componentsByType.soap.afterMain, 'soapAfterPagesContainer')]
			: []),
		...componentsByType.pinned[PINNED_POSITIONS.BOTTOM_LEFT].map((id) => createPinnedGroup(id)),
		...componentsByType.pinned[PINNED_POSITIONS.BOTTOM_CENTER].map((id) => createPinnedGroup(id)),
		...componentsByType.pinned[PINNED_POSITIONS.BOTTOM_RIGHT].map((id) => createPinnedGroup(id)),
		...(componentsByType.structural.includes('SITE_FOOTER') ? [createStructuralGroup('SITE_FOOTER')] : []),
		...(componentsByType.structural.includes('BACK_TO_TOP_BUTTON')
			? [createStructuralGroup('BACK_TO_TOP_BUTTON')]
			: []),
		...(componentsByType.structural.includes('QUICK_ACTION_BAR')
			? [createStructuralGroup('QUICK_ACTION_BAR')]
			: []),
	]
}

const createNonStructuralComp = (group: MasterPageGroup) => {
	return {
		id: group.id,
		components: group.components,
		componentType: group.type === GROUP_TYPES.PINNED ? 'PinnedLayer' : 'MeshGroup',
		layout: {},
		type: 'Container',
		parent: 'masterPage',
		styleId: '',
		metaData: {},
		skin: '',
	} as Component
}

const addGroupedCompsToStructure = (
	structureComps: Structure,
	masterPageChildrenGroups: Array<MasterPageGroup>,
	childrenLayout: { [compId: string]: Component['layout'] }
) => {
	_.forEach(masterPageChildrenGroups, (group) => {
		if (group.id) {
			// nonStructuralGroup
			structureComps[group.id as string] = createNonStructuralComp(group)
		} else {
			const compId = group.components[0]
			if (shouldAddPlaceholder(compId, childrenLayout[compId])) {
				const placeholderId = `${compId}-placeholder`
				structureComps[placeholderId] = {
					id: placeholderId,
					componentType: 'DivWithChildren',
					layout: { height: childrenLayout[compId].height },
					type: 'Component',
					parent: 'masterPage',
				} as Component
			}
		}
	})

	return structureComps
}

const convertMasterPageGroupsToStructure = (
	masterPage: Structure['masterPage'],
	masterPageGroups: Array<MasterPageGroup>,
	childrenLayout: { [compId: string]: Component['layout'] }
): Structure => {
	const masterPageChildrenGroups = _.flatMap(masterPageGroups, (childrenGroup) => {
		const compId = childrenGroup.components[0]
		if (shouldAddPlaceholder(compId, childrenLayout[compId])) {
			return {
				...childrenGroup,
				components: childrenGroup.components.concat(`${compId}-placeholder`),
			}
		}
		return childrenGroup
	})

	const masterPageChildren = _.flatMap(masterPageChildrenGroups, (group) =>
		group.type === GROUP_TYPES.SOAP || group.type === GROUP_TYPES.PINNED ? group.id : group.components
	)
	const structureComps = {
		masterPage: { ...masterPage, components: masterPageChildren },
	} as Structure
	return addGroupedCompsToStructure(structureComps, masterPageChildrenGroups, childrenLayout)
}
export {
	GROUP_TYPES,
	getMasterPageChildrenGroups,
	convertMasterPageGroupsToStructure,
	getMasterPageChildrenGroupsForMesh,
}
